import React, { useEffect, useState } from 'react';
import { doc, getDoc, updateDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db, storage } from '../../firebaseConfig';
import { ref, uploadBytesResumable, getDownloadURL, uploadBytes } from 'firebase/storage';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Editor } from '@tinymce/tinymce-react';
import { slugify } from '../../utils/slugify';
import 'tinymce/tinymce';
import 'tinymce/icons/default';
import 'tinymce/themes/silver';
import 'tinymce/models/dom';
import 'tinymce/skins/ui/oxide/skin.min.css';
import 'tinymce/skins/content/default/content.min.css';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/image';
import 'tinymce/plugins/table';
import 'tinymce/plugins/preview';
import 'tinymce/plugins/code';
import 'tinymce/plugins/fullscreen';

const CATEGORY_OPTIONS = [
  'Edukasi Ruqyah',
  'Gangguan & Keluhan',
  'Thibbun Nabawi',
  'Umum'
];

export default function AdminPostEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: '',
    image: '',
    status: 'draft',
    slug: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const docRef = doc(db, 'articles', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setForm({
            title: data.title || '',
            excerpt: data.excerpt || '',
            content: data.content || '',
            category: data.category || '',
            image: data.image || '',
            status: data.status || 'draft',
            slug: data.slug || ''
          });
        } else {
          setError('Artikel tidak ditemukan');
        }
      } catch (err) {
        setError('Gagal mengambil data: ' + err.message);
      }
    };

    fetchArticle();
  }, [id]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const uploadImageIfNeeded = () => {
    return new Promise((resolve, reject) => {
      if (!imageFile) return resolve(form.image || '');
      const storageRef = ref(storage, `article-images/${Date.now()}_${imageFile.name}`);
      const uploadTask = uploadBytesResumable(storageRef, imageFile);

      uploadTask.on('state_changed', snapshot => {
        const pct = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        setProgress(pct);
      }, err => reject(err), async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        resolve(url);
      });
    });
  };

  const handleEditorImageUpload = async (blobInfo) => {
    const filename = `article-content/${Date.now()}_${blobInfo.filename()}`;
    const storageRef = ref(storage, filename);
    await uploadBytes(storageRef, blobInfo.blob());
    const url = await getDownloadURL(storageRef);
    return url;
  };

  const ensureSlug = async () => {
    if (form.slug) return form.slug;
    const base = slugify(form.title) || id || `artikel-${Date.now().toString(36)}`;
    for (let attempt = 0; attempt < 5; attempt += 1) {
      const candidate = attempt === 0 ? base : `${base}-${Date.now().toString(36).slice(-4)}`;
      const docRef = doc(db, 'articles', candidate);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists() && docSnap.id !== id) continue;
      const q = query(collection(db, 'articles'), where('slug', '==', candidate), limit(1));
      const existing = await getDocs(q);
      if (existing.empty || existing.docs[0].id === id) return candidate;
    }
    return `${base}-${Date.now().toString(36).slice(-6)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.title.trim()) {
      setError('Judul wajib diisi.');
      return;
    }
    if (!form.category.trim()) {
      setError('Kategori wajib diisi.');
      return;
    }

    setLoading(true);
    try {
      const uploadedUrl = await uploadImageIfNeeded();
      const slug = await ensureSlug();
      const docRef = doc(db, 'articles', id);
      await updateDoc(docRef, { ...form, slug, image: uploadedUrl || '' });
      navigate('/admin/post');
    } catch (err) {
      setError('Gagal menyimpan perubahan: ' + err.message);
    }
    setLoading(false);
  };

  const resolvedCategories =
    form.category && !CATEGORY_OPTIONS.includes(form.category)
      ? [...CATEGORY_OPTIONS, `${form.category}`]
      : CATEGORY_OPTIONS;

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h2>Edit Artikel</h2>
          <p className="text-muted-small">Perbarui konten dan status publikasi.</p>
        </div>
      </div>

      <div className="admin-card">
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label className="form-label">Judul *</label>
          <input
            type="text"
            name="title"
            className="form-control"
            value={form.title}
            onChange={handleChange}
            disabled={loading}
            required
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Excerpt</label>
          <textarea
            name="excerpt"
            className="form-control"
            value={form.excerpt}
            onChange={handleChange}
            rows="2"
            disabled={loading}
          ></textarea>
        </div>

        <div className="mb-3">
          <label className="form-label">Konten</label>
          <div className="admin-editor">
            <Editor
              value={form.content}
              onEditorChange={value => setForm(prev => ({ ...prev, content: value }))}
              init={{
                height: 380,
                menubar: false,
                plugins: 'lists link image table preview code fullscreen',
                toolbar:
                  'undo redo | blocks | fontfamily fontsize | bold italic underline | alignleft aligncenter alignright | ' +
                  'bullist numlist | table | link image | removeformat | preview fullscreen',
                toolbar_mode: 'sliding',
                content_style:
                  'body { font-family: Inter, Segoe UI, Arial, sans-serif; font-size: 16px; line-height: 1.6; }',
                skin: false,
                content_css: false,
                automatic_uploads: true,
                images_upload_handler: async (blobInfo) => handleEditorImageUpload(blobInfo),
                promotion: false,
                branding: false,
              }}
            />
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label">Kategori *</label>
          <select
            name="category"
            className="form-select"
            value={form.category}
            onChange={handleChange}
            disabled={loading}
            required
          >
            <option value="">Pilih kategori</option>
            {resolvedCategories.map(item => (
              <option key={item} value={item}>{item}</option>
            ))}
          </select>
          <div className="form-text">Gunakan kategori agar artikel konsisten untuk SEO.</div>
        </div>

        <div className="mb-3">
          <label className="form-label">Upload Gambar (opsional)</label>
          <input
            type="file"
            accept="image/*"
            className="form-control"
            onChange={e => setImageFile(e.target.files[0])}
            disabled={loading}
          />
          <div className="form-text">URL saat ini: {form.image ? <a href={form.image} target="_blank" rel="noreferrer">lihat</a> : 'tidak ada'}</div>
        </div>

        {progress > 0 && progress < 100 && (
          <div className="mb-3">Mengupload gambar: {progress}%</div>
        )}

        <div className="mb-3">
          <label className="form-label">Status</label>
          <select
            name="status"
            className="form-select"
            value={form.status}
            onChange={handleChange}
            disabled={loading}
            required
          >
            <option value="draft">Draf</option>
            <option value="published">Terbit</option>
          </select>
        </div>

        <div className="admin-form-actions">
          <button type="submit" className="btn btn-success" disabled={loading}>
            {loading ? 'Menyimpan...' : 'Simpan Perubahan'}
          </button>
          <Link to="/admin/post" className="btn btn-outline-secondary">
            Batal
          </Link>
        </div>
      </form>
      </div>
    </div>
  );
}
